import * as Yup from "yup";
import { components } from "packages/api/generated-schemas/applicant-portal";
import {
  CustomQuestionnaireAnswerType,
  CustomQuestionnaireFormType,
} from "./types";

export function buildCustomQuestionnaireSchema(
  fields: readonly components["schemas"]["CustomQuestionnaireTemplateInputClaim"][],
  conditions: readonly components["schemas"]["CustomQuestionnaireTemplateInputClaimCondition"][],
  answers: CustomQuestionnaireAnswerType,
  t: (key: string) => string
): Yup.ObjectSchema<CustomQuestionnaireAnswerType> {
  const typeHandlers = (
    fieldType: Pick<
      components["schemas"]["CustomQuestionnaireTemplateInputClaim"],
      "type" | "is_optional"
    >
  ) => {
    let schema: Yup.AnySchema;
    if (fieldType.type === "int" || fieldType.type === "float") {
      schema = Yup.number().typeError(t("This field must be a number"));
    } else if (fieldType.type === "bool") {
      schema = Yup.boolean().transform((value, originalValue) => {
        if (originalValue === "true") return true;
        if (originalValue === "false") return false;
        return value;
      });
    } else if (fieldType.type === "str") {
      schema = Yup.string();
    } else {
      // Fallback for unhandled types, file_upload might be difficult to build a schema for
      schema = Yup.mixed();
    }

    if (fieldType.is_optional) {
      schema = schema.optional();
    } else {
      schema = schema.required(t("This field is required"));
    }
    return schema;
  };

  const shape: Record<string, Yup.AnySchema> = {};

  for (const field of fields) {
    const condition = conditions.find(
      (condition) => condition.dependent_claim_key === field.name
    );

    if (condition) {
      // Even if it's a conditional question, it could be optional.
      // Meaning we don't have to check if the condition is fulfilled.
      const isOptional = field.is_optional ?? false;

      if (isOptional) {
        shape[field.name] = typeHandlers({
          type: field.type,
          is_optional: true,
        });
      } else {
        const isFieldConditionFulfilled = isConditionFulfilled({
          condition,
          answers,
        });

        shape[field.name] = typeHandlers({
          type: field.type,
          is_optional: !isFieldConditionFulfilled,
        });
      }
    } else {
      shape[field.name] = typeHandlers({
        type: field.type,
        is_optional: field.is_optional,
      });
    }
  }

  return Yup.object().shape(shape);
}

export const buildCustomQuestionnaireFormSchema = (
  fields: readonly components["schemas"]["CustomQuestionnaireTemplateInputClaim"][],
  conditions: readonly components["schemas"]["CustomQuestionnaireTemplateInputClaimCondition"][],
  answers: CustomQuestionnaireAnswerType,
  t: (key: string) => string
): Yup.ObjectSchema<CustomQuestionnaireFormType> => {
  return Yup.object({
    templateId: Yup.string().required(),
    name: Yup.string().required(),
    isCurrent: Yup.boolean().required(),
    answers: buildCustomQuestionnaireSchema(fields, conditions, answers, t),
    claims: Yup.array(
      Yup.object({
        name: Yup.string().required(),
        type: Yup.mixed<
          "str" | "int" | "float" | "bool" | "file_upload"
        >().required(),
        label: Yup.string().required(),
        is_optional: Yup.boolean(),
        description: Yup.string().optional(),
        constraints: Yup.array(
          Yup.object({
            type: Yup.string().required(),
            selectable_options: Yup.array(
              Yup.object({
                value: Yup.string().required(),
                label: Yup.string().required(),
              }).required()
            ).required(),
          })
        ),
        display_options: Yup.object({
          multiline: Yup.boolean().optional(),
          widget: Yup.string().optional(),
        }).optional(),
      })
    ).required(),
    conditions: Yup.array(
      Yup.object({
        id: Yup.string().required(),
        parent_claim_id: Yup.string().required(),
        parent_claim_key: Yup.string().required(),
        condition_value: Yup.string().required(),
        dependent_claim_key: Yup.string().required(),
      }).required()
    ).optional(),
  });
};

export const isConditionFulfilled = (input: {
  condition: components["schemas"]["CustomQuestionnaireTemplateInputClaimCondition"];
  answers: CustomQuestionnaireAnswerType;
}): boolean => {
  const { condition, answers } = input;

  const parent_claim_key = condition.parent_claim_key;
  const parent_question_answer = answers[parent_claim_key];
  const stringified_condition_value = condition.condition_value.toString();

  const isConditionFulfilled =
    !!parent_question_answer &&
    typeof parent_question_answer === "string" &&
    parent_question_answer === stringified_condition_value;

  return isConditionFulfilled;
};
